import React, { useState } from 'react';
import LinkedModelsRow from './LinkedModelsRow';
import { TLinkedModel, TUpsertISRProjectDataModelsInput } from "../../Pages/DefineModel/model";
import DeleteProjectModal from "../DeleteProjectModal";
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';

type ModelOption = {
    value: string;
    label: string;
};

type LinkedModelsTableProps = {
    linkedModels: TLinkedModel[];
    modelOptions: ModelOption[];
    modelsLoading: boolean;
    onAddLinkedModel: () => void;
    onDeleteLinkedModel: (rowId: string) => void;
    onUpdateLinkedModel: (rowId: string, updates: Partial<TLinkedModel>) => void;
    onOpenFieldSelector: (rowId: string) => void;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
}

const LinkedModelsTable = ({
                               linkedModels,
                               modelOptions,
                               modelsLoading,
                               onAddLinkedModel,
                               onDeleteLinkedModel,
                               onUpdateLinkedModel,
                               onOpenFieldSelector,
                               userProjectDataModels
                           }: LinkedModelsTableProps) => {
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    const handleDeleteClick = (linkedModelId: string) => {
        setDeleteConfirmId(linkedModelId);
    };

    const linkedModelToDelete = linkedModels.find(lm => lm.rowId === deleteConfirmId);
    const modelNameForDelete = linkedModelToDelete?.name
        ?? modelOptions.find(m => m.value === linkedModelToDelete?.id)?.label;

    return (
        <div id="linked-models-table-container" className="tw-max-w-4xl">
            {/* Header with Add Linked Model button */}
            <div className="tw-flex tw-justify-end tw-items-center tw-mb-4">

                {/*<Typography variant="title-header" className="tw-text-grey-900">*/}
                {/*    Linked Model*/}
                {/*</Typography>*/}

                {/* Limited to one linked model for now */}
                {linkedModels.length === 0 && (
                    <Button id="linked-models-add-btn" variant="primary" onClick={onAddLinkedModel} className="!tw-rounded-[4px]">
                        Add Linked Model
                    </Button>
                )}
            </div>

            {/* Table */}
            {linkedModels.length > 0 ? (
                <div id="linked-models-table-wrapper" className="tw-bg-grey-100 tw-rounded-sm tw-p-5">
                    <div>
                        <table id="linked-models-table" className="tw-w-full tw-border-0 tw-table-fixed">
                            <thead>
                                <tr>
                                    <th className="tw-px-5 tw-py-3 tw-text-left tw-w-[28%]">
                                        <Typography variant="sub-body-supporting" className="tw-text-grey-900">
                                            Model Name
                                        </Typography>
                                    </th>
                                    <th className="tw-px-4 tw-py-3 tw-text-left tw-w-[32%]">
                                        <Typography variant="sub-body-supporting" className="tw-text-grey-900">
                                            Description
                                        </Typography>
                                    </th>
                                    <th className="tw-px-4 tw-py-3 tw-text-left tw-w-[32%]">
                                        <Typography variant="sub-body-supporting" className="tw-text-grey-900">
                                            Condition Field
                                        </Typography>
                                    </th>
                                    <th className="tw-px-4 tw-py-3 tw-w-[8%]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {linkedModels.map((linkedModel) => (
                                    <LinkedModelsRow
                                        key={linkedModel.rowId}
                                        linkedModel={linkedModel}
                                        modelOptions={modelOptions}
                                        modelsLoading={modelsLoading}
                                        onDelete={handleDeleteClick}
                                        onUpdate={onUpdateLinkedModel}
                                        onOpenFieldSelector={onOpenFieldSelector}
                                        userProjectDataModels={userProjectDataModels}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div id="linked-models-empty-state" className="tw-bg-grey-100 tw-rounded-sm tw-p-8 tw-text-center">
                    <svg className="tw-mx-auto tw-h-12 tw-w-12 tw-text-grey-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <Typography variant="title" className="tw-mt-2 tw-text-grey-900">
                        No linked model
                    </Typography>
                    <Typography variant="body-primary" className="tw-mt-1 tw-text-grey-600">
                        Get started by linking another model from this channel.
                    </Typography>
                    <div className="tw-mt-4 tw-flex tw-justify-center">
                        <Button id="linked-models-add-btn-empty" variant="secondary" onClick={onAddLinkedModel}>
                            Add Linked Model
                        </Button>
                    </div>
                </div>
            )}

            <DeleteProjectModal
                showModal={deleteConfirmId}
                closeModal={() => setDeleteConfirmId(null)}
                loading={false}
                onConfirm={async () => {
                    if (deleteConfirmId) {
                        onDeleteLinkedModel(deleteConfirmId);
                        setDeleteConfirmId(null);
                    }
                }}
                deleteItem={{ name: modelNameForDelete || "Linked Model" }}
                title={`Unlink Model ?`}
                message="This will remove the linked model. The linked model itself will not be deleted"
                confirmLabel="Unlink"
            />
        </div>
    );
};

export default LinkedModelsTable;
